import * as React from 'react'
import { cn } from '@/lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { SlashCommandMenu, DEFAULT_SLASH_COMMAND_GROUPS, type SlashCommandId } from '@/components/ui/slash-command-menu'
import { ChevronDown, X, Tag, Check, Zap } from 'lucide-react'
import { PERMISSION_MODE_CONFIG, type PermissionMode } from '@craft-agent/shared/agent/modes'
import type { BackgroundTask } from './ActiveTasksBar'
import { LabelIcon, LabelValueTypeIcon } from '@/components/ui/label-icon'
import { LabelValuePopover } from '@/components/ui/label-value-popover'
import type { LabelConfig } from '@craft-agent/shared/labels'
import { flattenLabels, parseLabelEntry, formatLabelEntry } from '@craft-agent/shared/labels'
import { resolveEntityColor } from '@craft-agent/shared/colors'
import { useTheme } from '@/context/ThemeContext'
import { useDynamicStack } from '@/hooks/useDynamicStack'
import type { SessionStatus } from '@/config/session-status-config'
import { getState } from '@/config/session-status-config'
import { SessionStatusMenu } from '@/components/ui/session-status-menu'
import { Command as CommandPrimitive } from 'cmdk'
import { extractLabelId } from '@craft-agent/shared/labels'
import { SkillAvatar } from '@/components/ui/skill-avatar'
import { AGENTS_PLUGIN_NAME } from '@craft-agent/shared/skills/types'
import type { LoadedSkill } from '@/shared/types'

import { MetadataBadge } from '@/components/ui/metadata-badge'
import { SHOW_STATUS } from '@/config/feature-flags'
import { isIPad } from '@/lib/platform'

// ============================================================================
// Permission Mode Icon Component
// ============================================================================

function PermissionModeIcon({ mode, className }: { mode: PermissionMode; className?: string }) {
  const config = PERMISSION_MODE_CONFIG[mode]
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d={config.svgPath} />
    </svg>
  )
}

export interface ActiveOptionBadgesProps {
  /** Current permission mode */
  permissionMode?: PermissionMode
  /** Callback when permission mode changes */
  onPermissionModeChange?: (mode: PermissionMode) => void
  /** Background tasks to display */
  tasks?: BackgroundTask[]
  /** Session ID for opening preview windows */
  sessionId?: string
  /** Absolute path to the session folder (for Files header actions) */
  sessionFolderPath?: string
  /** Callback when kill button is clicked on a task */
  onKillTask?: (taskId: string) => void
  /** Callback to set the input field value */
  onInsertMessage?: (text: string) => void
  /** Current input value (for appending skill mentions) */
  inputValue?: string
  /** Label entries applied to this session (e.g., ["bug", "priority::3"]) */
  sessionLabels?: string[]
  /** Available label configs (tree structure) for resolving label display */
  labels?: LabelConfig[]
  /** Callback when a label is removed (legacy — prefer onLabelsChange) */
  onRemoveLabel?: (labelId: string) => void
  /** Callback when session labels array changes (value edits or removals) */
  onLabelsChange?: (updatedLabels: string[]) => void
  /** Label ID whose value popover should auto-open (set when a valued label is added via # menu) */
  autoOpenLabelId?: string | null
  /** Called after the auto-open has been consumed, so the parent can clear the signal */
  onAutoOpenConsumed?: () => void
  // ── Skills badge ──
  /** Available skills for the skill picker badge */
  skills?: LoadedSkill[]
  /** Workspace ID for skill icon loading and mention formatting */
  workspaceId?: string
  // ── State/status badge (in dynamic stack) ──
  /** Available workflow states */
  sessionStatuses?: SessionStatus[]
  /** Current session state ID */
  currentSessionStatus?: string
  /** Callback when state changes */
  onSessionStatusChange?: (stateId: string) => void
  /** Whether ultrathink mode is enabled */
  ultrathinkEnabled?: boolean
  /** Callback when ultrathink mode changes */
  onUltrathinkChange?: (enabled: boolean) => void
  /** Additional CSS classes */
  className?: string
}

/** Resolved label entry: config + parsed value + original index in sessionLabels */
interface ResolvedLabelEntry {
  config: LabelConfig
  rawValue?: string
  index: number
}

export function ActiveOptionBadges({
  permissionMode = 'allow-all',
  onPermissionModeChange,
  tasks = [],
  sessionId,
  sessionFolderPath,
  onKillTask,
  onInsertMessage,
  inputValue = '',
  sessionLabels = [],
  labels = [],
  onRemoveLabel,
  onLabelsChange,
  autoOpenLabelId,
  onAutoOpenConsumed,
  skills = [],
  workspaceId,
  sessionStatuses = [],
  currentSessionStatus,
  onSessionStatusChange,
  ultrathinkEnabled,
  onUltrathinkChange,
  className,
}: ActiveOptionBadgesProps) {
  // Resolve session label entries to their config objects + parsed values.
  // Entries may be bare IDs ("bug") or valued ("priority::3").
  // Preserves the raw value and original index for editing/removal.
  const resolvedLabels = React.useMemo((): ResolvedLabelEntry[] => {
    if (sessionLabels.length === 0 || labels.length === 0) return []
    const flat = flattenLabels(labels)
    const result: ResolvedLabelEntry[] = []
    for (let i = 0; i < sessionLabels.length; i++) {
      const parsed = parseLabelEntry(sessionLabels[i])
      const config = flat.find(l => l.id === parsed.id)
      if (config) {
        result.push({ config, rawValue: parsed.rawValue, index: i })
      }
    }
    return result
  }, [sessionLabels, labels])

  const hasLabels = resolvedLabels.length > 0

  // Resolve the current state from sessionStatuses for the badge display.
  // Every session always has a state — fall back to the default state (or 'todo')
  // when currentSessionStatus isn't explicitly set, matching SessionList's behavior.
  const effectiveStateId = currentSessionStatus || 'todo'
  const resolvedState = sessionStatuses.length > 0 ? getState(effectiveStateId, sessionStatuses) : undefined
  const hasState = SHOW_STATUS && !!resolvedState

  // Show the stacking container when there are labels (state badge is now rendered standalone on the left)
  const hasStackContent = hasLabels

  // Dynamic stacking with equal visible strips: ResizeObserver computes per-badge
  // margins directly on children. Wider badges get more negative margins so each
  // shows the same visible strip when stacked. No React re-renders needed.
  const stackRef = useDynamicStack({ gap: 8, minVisible: 20, reservedStart: 0 })

  // Only render if badges or tasks are active
  if (!permissionMode && tasks.length === 0 && !hasStackContent) {
    return null
  }

  return (
    <div className={cn("flex items-start gap-2 mb-2 px-px pt-px pb-0.5", className)}>
      {/* Left side: ultrathink */}
      <div className="flex items-start gap-2 min-w-0">
        {/* Ultrathink Badge */}
        {ultrathinkEnabled && (
          <button
            type="button"
            onClick={() => onUltrathinkChange?.(false)}
            className="h-[30px] pl-2.5 pr-2 text-xs font-medium rounded-[8px] flex items-center gap-1.5 shrink-0 transition-all bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-pink-600/10 hover:from-blue-600/15 hover:via-purple-600/15 hover:to-pink-600/15 shadow-tinted outline-none select-none"
            style={{ '--shadow-color': '147, 51, 234' } as React.CSSProperties}
          >
            <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
              Ultrathink
            </span>
            <X className="h-3 w-3 text-purple-500 opacity-60 hover:opacity-100 translate-y-px" />
          </button>
        )}
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Right side: Explore mode → Labels */}
      <div className="flex items-center gap-2 shrink-0">
        {permissionMode && (
          <PermissionModeDropdown
            permissionMode={permissionMode}
            ultrathinkEnabled={ultrathinkEnabled}
            onPermissionModeChange={onPermissionModeChange}
            onUltrathinkChange={onUltrathinkChange}
          />
        )}


        {/* Label badges */}
        {hasStackContent && (
          <div
            className="min-w-0 max-w-[46vw] py-0.5 -my-0.5"
            style={{
              filter: 'drop-shadow(0px 0px 0.5px rgba(var(--foreground-rgb), 0.3)) drop-shadow(0px 1px 0.1px rgba(0,0,0,0.04)) drop-shadow(0px 3px 0.2px rgba(0,0,0,0.03))',
            }}
          >
            <div
              ref={stackRef}
              className="flex items-center min-w-0 py-1 -my-1"
              style={{ overflow: 'clip' }}
            >
              {resolvedLabels.map(({ config, rawValue, index }) => (
                <LabelBadge
                  key={`${config.id}-${index}`}
                  label={config}
                  value={rawValue}
                  autoOpen={config.id === autoOpenLabelId}
                  onAutoOpenConsumed={onAutoOpenConsumed}
                  onValueChange={(newValue) => {
                    const updated = [...sessionLabels]
                    updated[index] = formatLabelEntry(config.id, newValue)
                    onLabelsChange?.(updated)
                  }}
                  onRemove={() => {
                    if (onLabelsChange) {
                      onLabelsChange(sessionLabels.filter((_, i) => i !== index))
                    } else {
                      onRemoveLabel?.(config.id)
                    }
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// Label Badge Component
// ============================================================================

/**
 * Format a raw value for display based on the label's valueType.
 * Dates render as locale short format; numbers and strings pass through.
 */
function formatDisplayValue(rawValue: string, valueType?: 'string' | 'number' | 'date'): string {
  if (valueType === 'date') {
    const date = new Date(rawValue.includes('T') ? rawValue + ':00Z' : rawValue + 'T00:00:00Z')
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    }
  }
  return rawValue
}

/**
 * Renders a single label badge with LabelValuePopover for editing/removal.
 * No box-shadow on the badge itself — all shadows come from the parent
 * wrapper's drop-shadow filter (traces masked alpha without clipping).
 * Shows: [color circle] [name] [· value in mono] [chevron]
 */
function LabelBadge({
  label,
  value,
  autoOpen,
  onAutoOpenConsumed,
  onValueChange,
  onRemove,
  sessionId,
}: {
  label: LabelConfig
  value?: string
  /** When true, auto-open the value popover on mount (for newly added valued labels) */
  autoOpen?: boolean
  onAutoOpenConsumed?: () => void
  onValueChange?: (newValue: string | undefined) => void
  onRemove: () => void
  sessionId?: string
}) {
  const { isDark } = useTheme()
  const [open, setOpen] = React.useState(false)

  // Auto-open the value popover when this label was just added via # menu
  // and has a valueType. Opens exactly once, then clears the signal.
  React.useEffect(() => {
    if (autoOpen && label.valueType) {
      setOpen(true)
      onAutoOpenConsumed?.()
    }
  }, [autoOpen, label.valueType, onAutoOpenConsumed])

  // Resolve label color for tinting background and text via CSS color-mix
  const resolvedColor = label.color
    ? resolveEntityColor(label.color, isDark)
    : 'var(--foreground)'

  const displayValue = value ? formatDisplayValue(value, label.valueType) : undefined

  return (
    <LabelValuePopover
      label={label}
      value={value}
      open={open}
      onOpenChange={setOpen}
      onValueChange={onValueChange}
      onRemove={onRemove}
      sessionId={sessionId}
    >
      <MetadataBadge
        label={label.name}
        value={displayValue}
        icon={<LabelIcon label={label} size="lg" />}
        valueHintIcon={label.valueType ? <LabelValueTypeIcon valueType={label.valueType} /> : undefined}
        badgeColor={resolvedColor}
        interactive
        isActive={open}
        showChevron
        shadow="none"
        className="relative"
      />
    </LabelValuePopover>
  )
}

// ============================================================================
// Label Picker Badge Component
// ============================================================================

/**
 * Renders a compact label badge next to the permission mode badge.
 * Shows the first applied label's name/color, or a generic "Label" with tag icon.
 * Click opens a label picker popover for toggling labels.
 */
function LabelPickerBadge({
  sessionLabels,
  labels,
  onLabelsChange,
}: {
  sessionLabels: string[]
  labels: LabelConfig[]
  onLabelsChange?: (updatedLabels: string[]) => void
}) {
  const [open, setOpen] = React.useState(false)
  const { isDark } = useTheme()

  const flatLabels = React.useMemo(() => flattenLabels(labels), [labels])
  const appliedLabelIds = React.useMemo(
    () => new Set(sessionLabels.map(extractLabelId)),
    [sessionLabels]
  )

  // Find the first applied label for display
  const firstLabel = React.useMemo(() => {
    if (sessionLabels.length === 0) return undefined
    const firstId = extractLabelId(sessionLabels[0])
    return flatLabels.find(l => l.id === firstId)
  }, [sessionLabels, flatLabels])

  const handleToggle = React.useCallback((labelId: string) => {
    if (!onLabelsChange) return
    const isApplied = appliedLabelIds.has(labelId)
    if (isApplied) {
      const updated = sessionLabels.filter(entry => extractLabelId(entry) !== labelId)
      onLabelsChange(updated)
    } else {
      onLabelsChange([...sessionLabels, labelId])
    }
  }, [sessionLabels, appliedLabelIds, onLabelsChange])

  const badgeColor = firstLabel?.color
    ? resolveEntityColor(firstLabel.color, isDark)
    : 'var(--foreground)'

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "h-[30px] pl-2.5 pr-2 text-xs font-medium rounded-[8px] flex items-center gap-1.5 shrink-0",
            "outline-none select-none transition-colors shadow-minimal",
            "bg-[color-mix(in_srgb,var(--background)_97%,var(--badge-color))]",
            "hover:bg-[color-mix(in_srgb,var(--background)_92%,var(--badge-color))]",
            "text-[color-mix(in_srgb,var(--foreground)_80%,var(--badge-color))]",
          )}
          style={{ '--badge-color': badgeColor } as React.CSSProperties}
        >
          <span className="shrink-0 flex items-center">
            {firstLabel ? (
              <LabelIcon label={firstLabel} size="lg" />
            ) : (
              <Tag className="w-3 h-3" />
            )}
          </span>
          <span className="whitespace-nowrap">
            {firstLabel ? firstLabel.name : 'Label'}
          </span>
          <ChevronDown className="h-3.5 w-3.5 opacity-40" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 border-0 shadow-none bg-transparent"
        side="top"
        align="end"
        sideOffset={4}
        onCloseAutoFocus={(e) => {
          e.preventDefault()
          window.dispatchEvent(new CustomEvent('craft:focus-input'))
        }}
      >
        <CommandPrimitive
          className="min-w-[180px] overflow-hidden rounded-[8px] bg-background text-foreground shadow-modal-small"
        >
          <div className="border-b border-border/50 px-3 py-2">
            <CommandPrimitive.Input
              placeholder="Filter labels..."
              className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground/50"
              autoFocus={!isIPad}
            />
          </div>
          <CommandPrimitive.List className="max-h-[240px] overflow-y-auto p-1 [&_[cmdk-list-sizer]]:space-y-px">
            <CommandPrimitive.Empty className="py-3 text-center text-sm text-muted-foreground">
              No labels found
            </CommandPrimitive.Empty>
            {flatLabels.map((label) => {
              const isApplied = appliedLabelIds.has(label.id)
              return (
                <CommandPrimitive.Item
                  key={label.id}
                  value={label.name}
                  onSelect={() => handleToggle(label.id)}
                  className={cn(
                    "flex cursor-pointer select-none items-center gap-3 rounded-[6px] px-3 py-1.5 text-[13px] outline-none",
                    isApplied ? "bg-foreground/7" : "data-[selected=true]:bg-foreground/3"
                  )}
                >
                  <LabelIcon label={label} size="lg" />
                  <div className="flex-1 min-w-0">{label.name}</div>
                  {isApplied && (
                    <Check className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  )}
                </CommandPrimitive.Item>
              )
            })}
          </CommandPrimitive.List>
        </CommandPrimitive>
      </PopoverContent>
    </Popover>
  )
}

// ============================================================================
// Skill Picker Badge Component
// ============================================================================

/**
 * Renders a compact skill badge next to the label badge.
 * Click opens a skill picker popover; selecting a skill inserts a
 * [skill:...] mention into the input field via onInsertMessage.
 */
function SkillPickerBadge({
  skills,
  workspaceId,
  inputValue = '',
  onInsertMessage,
}: {
  skills: LoadedSkill[]
  workspaceId?: string
  inputValue?: string
  onInsertMessage?: (text: string) => void
}) {
  const [open, setOpen] = React.useState(false)

  const handleSelect = React.useCallback((skill: LoadedSkill) => {
    if (!onInsertMessage) return
    const pluginName = skill.source === 'workspace' ? workspaceId : AGENTS_PLUGIN_NAME
    const qualifiedName = pluginName ? `${pluginName}:${skill.slug}` : skill.slug
    const mention = `[skill:${qualifiedName}] `
    // Append the mention to the current input value instead of replacing it
    const separator = inputValue.length > 0 && !inputValue.endsWith(' ') ? ' ' : ''
    onInsertMessage(inputValue + separator + mention)
    setOpen(false)
  }, [onInsertMessage, workspaceId, inputValue])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "h-[30px] pl-2.5 pr-2 text-xs font-medium rounded-[8px] flex items-center gap-1.5 shrink-0",
            "outline-none select-none transition-colors shadow-minimal",
            "bg-foreground/5 text-foreground/60",
            "hover:bg-foreground/8",
          )}
        >
          <Zap className="w-3 h-3" />
          <span className="whitespace-nowrap">Skill</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-40" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 border-0 shadow-none bg-transparent"
        side="top"
        align="end"
        sideOffset={4}
        onCloseAutoFocus={(e) => {
          e.preventDefault()
          window.dispatchEvent(new CustomEvent('craft:focus-input'))
        }}
      >
        <CommandPrimitive
          className="min-w-[220px] overflow-hidden rounded-[8px] bg-background text-foreground shadow-modal-small"
        >
          <div className="border-b border-border/50 px-3 py-2">
            <CommandPrimitive.Input
              placeholder="Filter skills..."
              className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground/50"
              autoFocus={!isIPad}
            />
          </div>
          <CommandPrimitive.List className="max-h-[240px] overflow-y-auto p-1 [&_[cmdk-list-sizer]]:space-y-px">
            <CommandPrimitive.Empty className="py-3 text-center text-sm text-muted-foreground">
              No skills found
            </CommandPrimitive.Empty>
            {skills.map((skill) => (
              <CommandPrimitive.Item
                key={skill.slug}
                value={skill.metadata.name}
                onSelect={() => handleSelect(skill)}
                className="flex cursor-pointer select-none items-center gap-3 rounded-[6px] px-3 py-1.5 text-[13px] outline-none data-[selected=true]:bg-foreground/3"
              >
                <SkillAvatar skill={skill} size="sm" workspaceId={workspaceId} />
                <div className="flex-1 min-w-0 truncate">{skill.metadata.name}</div>
              </CommandPrimitive.Item>
            ))}
          </CommandPrimitive.List>
        </CommandPrimitive>
      </PopoverContent>
    </Popover>
  )
}

// ============================================================================
// State Badge Component
// ============================================================================

/**
 * Renders the current workflow state as a badge in the dynamic stacking container.
 * Click opens a SessionStatusMenu popover for changing the state.
 * Styled consistently with label badges (h-[30px], rounded-[8px], color-mix tinting).
 */
function StateBadge({
  state,
  sessionStatuses,
  onSessionStatusChange,
  sessionId,
}: {
  state: SessionStatus
  sessionStatuses: SessionStatus[]
  onSessionStatusChange?: (stateId: string) => void
  sessionId?: string
}) {
  const [open, setOpen] = React.useState(false)

  const handleSelect = React.useCallback((stateId: string) => {
    setOpen(false)
    onSessionStatusChange?.(stateId)
  }, [onSessionStatusChange])

  // Use the state's resolved color for tinting (same color-mix pattern as labels)
  const badgeColor = state.resolvedColor || 'var(--foreground)'
  const applyColor = state.iconColorable

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <MetadataBadge
          label={state.label}
          badgeColor={badgeColor}
          interactive
          isActive={open}
          showChevron
          icon={(
            <span
              className="shrink-0 flex items-center w-3.5 h-3.5 [&>svg]:w-full [&>svg]:h-full [&>img]:w-full [&>img]:h-full [&>span]:text-xs"
              style={applyColor ? { color: state.resolvedColor } : undefined}
            >
              {state.icon}
            </span>
          )}
          className="pl-2.5"
        />
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 border-0 shadow-none bg-transparent"
        side="top"
        align="end"
        sideOffset={4}
        onCloseAutoFocus={(e) => {
          e.preventDefault()
          window.dispatchEvent(new CustomEvent('craft:focus-input', {
            detail: { sessionId }
          }))
        }}
      >
        <SessionStatusMenu
          activeState={state.id}
          onSelect={handleSelect}
          states={sessionStatuses}
        />
      </PopoverContent>
    </Popover>
  )
}

interface PermissionModeDropdownProps {
  permissionMode: PermissionMode
  ultrathinkEnabled?: boolean
  onPermissionModeChange?: (mode: PermissionMode) => void
  onUltrathinkChange?: (enabled: boolean) => void
}

function PermissionModeDropdown({ permissionMode, ultrathinkEnabled, onPermissionModeChange, onUltrathinkChange }: PermissionModeDropdownProps) {
  const [open, setOpen] = React.useState(false)
  // Optimistic local state - updates immediately, syncs with prop
  const [optimisticMode, setOptimisticMode] = React.useState(permissionMode)

  // Sync optimistic state when prop changes (confirmation from backend)
  React.useEffect(() => {
    setOptimisticMode(permissionMode)
  }, [permissionMode])

  const activeCommands = React.useMemo((): SlashCommandId[] => {
    return [optimisticMode as SlashCommandId]
  }, [optimisticMode])

  // Handle command selection from dropdown
  const handleSelect = React.useCallback((commandId: SlashCommandId) => {
    if (commandId === 'safe' || commandId === 'allow-all') {
      setOptimisticMode(commandId)
      onPermissionModeChange?.(commandId)
    }
    setOpen(false)
  }, [onPermissionModeChange])

  // Get config for current mode (use optimistic state for instant UI update)
  const config = PERMISSION_MODE_CONFIG[optimisticMode]

  // Mode-specific styling using CSS variables (theme-aware)
  // - safe (Cautious): foreground at 60% opacity - subtle, read-only feel
  // - allow-all (Autonomous): accent color - purple, full autonomy
  const modeStyles: Record<PermissionMode, { className: string; shadowVar: string }> = {
    'safe': {
      className: 'bg-foreground/5 text-foreground/60',
      shadowVar: 'var(--foreground-rgb)',
    },
    'allow-all': {
      className: 'bg-accent/5 text-accent',
      shadowVar: 'var(--accent-rgb)',
    },
  }
  const currentStyle = modeStyles[optimisticMode]

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-tutorial="permission-mode-dropdown"
          className={cn(
            "h-[30px] pl-2.5 pr-2 text-xs font-medium rounded-[8px] flex items-center gap-1.5 shadow-tinted outline-none select-none",
            currentStyle.className
          )}
          style={{ '--shadow-color': currentStyle.shadowVar } as React.CSSProperties}
        >
          <PermissionModeIcon mode={optimisticMode} className="h-3.5 w-3.5" />
          <span>{config.displayName}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 rounded-[8px] bg-background text-foreground shadow-modal-small"
        side="top"
        align="start"
        sideOffset={4}
        onCloseAutoFocus={(e) => {
          e.preventDefault()
          window.dispatchEvent(new CustomEvent('craft:focus-input'))
        }}
      >
        <SlashCommandMenu
          commandGroups={DEFAULT_SLASH_COMMAND_GROUPS}
          activeCommands={activeCommands}
          onSelect={handleSelect}
          showFilter
        />
      </PopoverContent>
    </Popover>
  )
}

